import { Box, Flex, Heading } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { getHitterOption } from 'components/common/hitters/hitter-option';
import { getPitch } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/common';
import { MachineShotDetails } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/machine-shot-details';
import { MachineShotHistoryTable } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/machine-shot-history-table';
import { MissedVisionDetections } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/missed-vision-detections';
import { HistoryTabSelector } from 'components/sections/admin-portal/machines/dialogs/settings/machine-shot-history/tab-selector';
import { useMachineShotHistoryDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineShotHistoryDialogState';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MACHINE_SETTINGS_CONTENT_ID } from 'enums/admin-portal';
import { HistoryTab } from 'enums/machine-settings';
import useLatest from 'hooks/useLatestRef';
import usePrevious from 'hooks/usePrevious';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import {
  ShotHistoryTableConfidence,
  ShotHistoryTableMode,
  ShotHistoryTableStaticValues,
} from 'interfaces/i-machine-shots-history-table';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { PLAYER_LEVELS, PLAYER_LEVELS_DICT } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import {
  IPaginatedShotHistory,
  IShotHistoryConfidenceFilter,
} from 'lib_ts/interfaces/data/i-machine-shot-history';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { IMachineDetailsShotMsg } from 'lib_ts/interfaces/machine-msg/i-machine-details';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';
import { AdminTeamsService } from 'services/admin/teams.service';
import { PitchesService } from 'services/pitches.service';
import { ShotsService } from 'services/shots.service';
import { WebSocketService } from 'services/web-socket.service';
import { useAuthStore } from 'stores/use-auth-store';

const RECORDS_PER_REQUEST = 25;
const COMPONENT_NAME = 'MachineShotHistory';
const MIN_PITCH_NAME_QUERY_LENGTH = 2;

// to enable when FW ships their side
const SHOW_MISSED_VISION_DETECTIONS_TAB = true;
const NO_LEVEL_GROUP = 'No Level';

const buildHitterOptions = (hitters: IHitter[]): IOptionExt[] => {
  const options: IOptionExt[] = [];

  for (const level of PLAYER_LEVELS) {
    const group = `${t('common.level')}: ${t(level.label)}`;

    hitters
      .filter((h) => h.level === level.value)
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((h) => {
        const option = getHitterOption(h, group);

        if (h.customID) {
          option.aliases = [...(option.aliases ?? []), h.customID];
        }

        options.push(option);
      });
  }

  hitters
    .filter((h) => !h.level || !PLAYER_LEVELS_DICT[h.level])
    .sort((a, b) => a.name.localeCompare(b.name))
    .forEach((h) => {
      const option = getHitterOption(h, NO_LEVEL_GROUP);

      if (h.customID) {
        option.aliases = [...(option.aliases ?? []), h.customID];
      }

      options.push(option);
    });

  return options;
};

export const ShotHistory = () => {
  const wsConnected = useAuthStore((s) => s.wsConnected);
  const machine = useMachinesPortalStore((s) => s.machine);

  const {
    shotID,
    shotFilters,
    historyTab,
    setShotID,
    resetShotID,
    setShotFilter,
  } = useMachineShotHistoryDialogState();

  const initializedRef = useRef(false);
  const prevFilters = usePrevious(shotFilters);
  const latestSetShotFilter = useLatest(setShotFilter);
  const latestShotFilters = useLatest(shotFilters);

  const [hittersBootstrapped, setHittersBootstrapped] = useState(false);
  const [hitterOptions, setHitterOptions] = useState<IOptionExt[]>([]);

  const canFilterByHitter = !!machine?._parent_id && hittersBootstrapped;

  const [loading, setLoading] = useState(false);

  const [hasMore, setHasMore] = useState(false);
  const [shots, setShots] = useState<IMachineShot[]>([]);
  const latestShots = useLatest(shots);

  // ID of shots that were recently added/updated via WS for row highlighting
  // todo: should we queue these up to highlight them when the table comes back into view after drill down?
  const [newShotID, setNewShotID] = useState<string>('');
  const [updatedShotID, setUpdatedShotID] = useState<string>('');

  // key is pitch _id
  const [pitchDict, setPitchDict] = useState<DictOf<IPitch>>({});
  // key is session-hit _id, value is hitter name (if available)
  const [hitterDict, setHitterDict] = useState<DictOf<{ name: string }>>({});

  // show ShotDetails when a shot is selected (shot ID stored in URL params via setShotID)
  const [selectedShot, setSelectedShot] = useState<IMachineShot>();
  const latestSelectedShot = useLatest(selectedShot); // stored in ref to avoid adding to WS useEffect deps

  // used to persist table scroll position when navigating to/from error details
  const scrollPositionRef = useRef<number>();

  const loadData = useCallback(
    async (config?: {
      cursorCreated?: string;
      silently?: boolean;
    }): Promise<IPaginatedShotHistory | undefined> => {
      if (!machine?.machineID) {
        return;
      }

      try {
        const { cursorCreated, silently } = config ?? {};

        if (!silently) {
          setLoading(true);
        }

        const coercedDateRange = ():
          | { start_date: string; end_date: string }
          | undefined => {
          if (!shotFilters.dateRange) {
            return undefined;
          }

          return {
            start_date: shotFilters.dateRange.start.toISOString(),
            end_date: shotFilters.dateRange.end.toISOString(),
          };
        };

        const coercedTrainingMode = (): Nullable<boolean> => {
          if (shotFilters.mode === '') {
            return null;
          }

          // 'Training' -> true
          if (shotFilters.mode === ShotHistoryTableMode.Training) {
            return true;
          }

          // 'BP' -> false
          return false;
        };

        const coercedHasVideo = (): boolean | undefined => {
          if (shotFilters.hasVideo === '') {
            return undefined;
          }

          // 'True' -> true
          if (shotFilters.hasVideo === ShotHistoryTableStaticValues.True) {
            return true;
          }

          // 'False' -> false
          return false;
        };

        const coercedConfidence = ():
          | IShotHistoryConfidenceFilter
          | undefined => {
          if (shotFilters.confidence === '') {
            return undefined;
          }

          // 'High' -> 1
          if (shotFilters.confidence === ShotHistoryTableConfidence.High) {
            return 1;
          }

          // 'Low' -> 0
          return 0;
        };

        const coercedPitchName = () => {
          const sanitizedPitchName = shotFilters.pitchName.trim();
          return sanitizedPitchName.length < MIN_PITCH_NAME_QUERY_LENGTH
            ? undefined
            : sanitizedPitchName;
        };

        const coercedHitterID = (): string | undefined => {
          const id = shotFilters.hitterID.trim();
          if (!id || !machine?._parent_id) {
            return undefined;
          }

          return id;
        };

        const dates = coercedDateRange();

        const response =
          await AdminMachinesService.getInstance().getShotHistory({
            machineID: machine.machineID,
            training: coercedTrainingMode(),
            confidence: coercedConfidence(),
            has_video: coercedHasVideo(),
            pitch_name: coercedPitchName(),
            hitter_id: coercedHitterID(),
            start_date: dates?.start_date,
            end_date: dates?.end_date,
            limit: RECORDS_PER_REQUEST,
            check_confidence: false,
            check_spin: false,
            check_breaks: false,
            cursor_created: cursorCreated,
          });

        const { has_more: more, data: latest } = response;

        setHasMore(more);
        setPitchDict((prev) => ({
          ...prev,
          ...latest.pitchDict,
        }));
        setHitterDict((prev) => ({
          ...prev,
          ...latest.hitterDict,
        }));

        setNewShotID('');
        setUpdatedShotID('');

        if (cursorCreated) {
          setShots((prev) => [...prev, ...latest.shots]);
          return response;
        }

        setShots(latest.shots);
        return response;
      } catch (e) {
        NotifyHelper.error({
          message_md: `Unable to load machine shot history: ${e}`,
        });
      } finally {
        // react update batching causing some annoying flickering
        setTimeout(() => {
          setLoading(false);
        }, 0);
      }
    },
    [
      // deeper comparison of shotFilters for efficiency
      shotFilters.dateRange,
      shotFilters.mode,
      shotFilters.hasVideo,
      shotFilters.confidence,
      shotFilters.pitchName,
      shotFilters.hitterID,
      machine?.machineID,
      machine?._parent_id,
    ]
  );

  const loadDataRef = useLatest(loadData);

  useEffect(() => {
    initializedRef.current = false;
    setHittersBootstrapped(false);

    if (!machine?.machineID) {
      setHitterOptions([]);
      return;
    }

    const bootstrapHitters = async () => {
      if (!machine._parent_id) {
        if (latestShotFilters.current.hitterID) {
          latestSetShotFilter.current({ hitterID: '' });
        }

        setHitterOptions([]);
        setHittersBootstrapped(true);
        return;
      }

      const hitters = await AdminTeamsService.getInstance().getTeamHitters(
        machine._parent_id
      );

      setHitterOptions(buildHitterOptions(hitters));

      const validHitterIDs = new Set(hitters.map((h) => h._id));
      if (
        latestShotFilters.current.hitterID &&
        !validHitterIDs.has(latestShotFilters.current.hitterID)
      ) {
        latestSetShotFilter.current({ hitterID: '' });
      }

      setHittersBootstrapped(true);
    };

    bootstrapHitters();
  }, [machine?.machineID, machine?._parent_id]);

  useEffect(() => {
    if (!hittersBootstrapped || initializedRef.current || !machine?.machineID) {
      return;
    }

    initializedRef.current = true;
    loadDataRef.current();
  }, [hittersBootstrapped, machine?.machineID]);

  const hasFilterSet = useMemo(
    () => Object.values(shotFilters).some((v) => !!v),
    [shotFilters]
  );

  // real-time update for new shots
  useEffect(() => {
    if (!wsConnected) {
      return;
    }

    const upsertShot = async (event: CustomEvent) => {
      const data = event.detail as IMachineDetailsShotMsg;

      if (!data.shot) {
        return;
      }

      // just in case
      if (data.shot.machineID !== machine?.machineID) {
        return;
      }

      // race condition handling - just in case we get the WS message after we load via REST
      setShots((prev) => {
        if (!data.shot) {
          return prev;
        }

        const shotIndex = prev.findIndex((s) => s._id === data.shot?._id);

        if (data.operation === 'update' && shotIndex > -1) {
          const newShots = [...prev];
          newShots[shotIndex] = data.shot;
          return newShots;
        }

        return [data.shot, ...prev];
      });

      setPitchDict((prev) => {
        if (!data.pitch) {
          return prev;
        }

        // we already have this pitch cached
        if (prev[data.pitch._id]) {
          return prev;
        }

        return {
          ...prev,
          [data.pitch._id]: data.pitch,
        };
      });

      setHitterDict((prev) => {
        const fireId = data.shot?.fire_id;
        if (!fireId || !data.hitterName) {
          return prev;
        }

        return { ...prev, [fireId]: { name: data.hitterName } };
      });

      const prevShot = latestShots.current.find(
        (s) => s._id === data.shot?._id
      );

      if (data.operation === 'create' || !prevShot) {
        setNewShotID(data.shot._id);
        setUpdatedShotID('');
        return;
      }

      // handlers below are for 'update' of a shot already in the table

      // only flash the row when the annotated video first lands. Uploading the
      // raw, replay/stereo, and annotated videos each emit a shot update, but
      // the blue flash is reserved for annotation completion — so gate it on the
      // annotated video id appearing, not on playability (the Play button also
      // enables on the earlier replay upload via getFireVideoID).
      const wasAnnotated = !!prevShot?.annotated_video_id;
      const isAnnotated = !!data.shot.annotated_video_id;

      if (!wasAnnotated && isAnnotated) {
        setUpdatedShotID(data.shot._id);
        setNewShotID('');
      }

      // update the shot shown in MachineShotDetails if it's the one currently selected
      if (data.shot._id === latestSelectedShot.current?._id) {
        setSelectedShot(data.shot);
      }
    };

    const unsubscribe = () => {
      WebSocketHelper.remove(WsMsgType.S2U_MachineShotHistory, upsertShot);
    };

    // don't upsert rows if we're looking at filtered data
    if (hasFilterSet) {
      return unsubscribe;
    }

    WebSocketHelper.on(WsMsgType.S2U_MachineShotHistory, upsertShot);
    return unsubscribe;
  }, [machine?.machineID, hasFilterSet, wsConnected]);

  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }

    // queries can be expensive, so we'll do a manual equality check here just in case
    // todo: is this actually necessary?
    if (!prevFilters || isEqual(prevFilters, shotFilters)) {
      return;
    }

    loadData();
  }, [shotFilters, loadData]);

  const onSelect = (s?: IMachineShot) => {
    if (!s) {
      return;
    }

    // save our scroll position so we can restore it later when the user goes back to the table
    const contentEl = document.getElementById(MACHINE_SETTINGS_CONTENT_ID);
    scrollPositionRef.current = contentEl?.scrollTop;

    setSelectedShot(s);

    // sync up URL param with selected error
    setShotID(s._id);
  };

  const onBack = () => {
    setSelectedShot(undefined);

    // remove errorID from URL params
    resetShotID();
  };

  // primarily used when the user is visiting a direct link to an shot from outside the app
  useEffect(() => {
    // if we already have the shot selected, no need to fetch it again
    if (shotID === selectedShot?._id) {
      return;
    }

    if (!shotID) {
      setSelectedShot(undefined);
      return;
    }

    const shot = shots.find((s) => s._id === shotID);

    if (shot) {
      setSelectedShot(shot);
      return;
    }

    const lazyLoadPitch = async (id: string) => {
      const pitch = await PitchesService.getInstance().getOne(id);

      if (!pitch) {
        setPitchDict((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        return;
      }

      setPitchDict((prev) => ({
        ...prev,
        [pitch._id]: pitch,
      }));
    };

    const lazyLoadShot = async () => {
      const result = await ShotsService.getInstance().getById(shotID);

      if (!result) {
        NotifyHelper.warning({
          message_md: `No shot found with ID ${shotID}`,
        });

        resetShotID();
        return;
      }

      if (result.pitch_id) {
        await lazyLoadPitch(result.pitch_id);
      }

      setSelectedShot(result);
    };

    // if we don't have the shot in our current data set, fetch it from the server
    lazyLoadShot();
  }, [shotID, selectedShot?._id, shots]);

  const selectedShotHasPrev = useMemo(() => {
    if (!selectedShot) {
      return false;
    }

    const currentIndex = shots.findIndex((s) => s._id === selectedShot._id);

    if (currentIndex < 0) {
      return false;
    }

    return currentIndex < shots.length - 1 || hasMore;
  }, [selectedShot, shots, hasMore]);

  const selectedShotHasNext = useMemo(() => {
    if (!selectedShot) {
      return false;
    }

    const currentIndex = shots.findIndex((s) => s._id === selectedShot._id);

    if (currentIndex < 0) {
      return false;
    }

    return currentIndex > 0;
  }, [selectedShot, shots]);

  const onNextShot = () => {
    if (!selectedShot) {
      return;
    }

    const currentIndex = shots.findIndex((s) => s._id === selectedShot._id);
    const nextIndex = currentIndex - 1;

    if (nextIndex >= 0) {
      const nextShot = shots[nextIndex];
      setSelectedShot(nextShot);
      setShotID(nextShot._id);
    }
  };

  const onPrevShot = async () => {
    if (!selectedShot) {
      return;
    }

    const currentIndex = shots.findIndex((s) => s._id === selectedShot._id);
    if (currentIndex < 0) {
      return;
    }

    const prevIndex = currentIndex + 1;

    if (prevIndex < shots.length) {
      const prevShot = shots[prevIndex];
      setSelectedShot(prevShot);
      setShotID(prevShot._id);

      return;
    }

    if (hasMore) {
      const cursorCreated = shots[shots.length - 1]?._created;

      if (!cursorCreated) {
        return;
      }

      const newData = await loadData({ cursorCreated });
      if (!newData?.data.shots.length) {
        return;
      }

      const prevShot = newData.data.shots[0];
      setSelectedShot(prevShot);
      setShotID(prevShot._id);
    }
  };

  const onLoadMore = () => {
    const cursorCreated = shots[shots.length - 1]?._created;

    if (!cursorCreated) {
      return;
    }

    loadData({ cursorCreated, silently: true });
  };

  const showSection = !historyTab || historyTab === HistoryTab.ShotHistory;
  const showSelector =
    SHOW_MISSED_VISION_DETECTIONS_TAB &&
    machine?.enable_trajekt_vision &&
    showSection;

  return (
    <ErrorBoundary identifier="MachineShotHistory">
      <Flex
        className={StringHelper.classNames([
          RADIX.VFLEX.GROW,
          showSection ? '' : 'visibility-hidden position-absolute',
        ])}
        direction="column"
      >
        {selectedShot && (
          <Box>
            <MachineShotDetails
              shot={selectedShot}
              pitch={getPitch(pitchDict, selectedShot.pitch_id)}
              onBack={onBack}
              onPrevShot={onPrevShot}
              onNextShot={onNextShot}
              hasPrev={selectedShotHasPrev}
              hasNext={selectedShotHasNext}
              loadingPrev={loading}
            />
          </Box>
        )}
        <Flex
          className={StringHelper.classNames([
            RADIX.VFLEX.GROW,
            /* always keep table in the DOM to persist input states */
            selectedShot ? 'visibility-hidden' : '',
          ])}
          direction="column"
          gap={RADIX.FLEX.GAP.SM}
        >
          <Flex justify="between" align="center">
            <Heading size="4">Shot History</Heading>
            {showSelector && <HistoryTabSelector />}
          </Flex>
          <MachineShotHistoryTable
            shots={shots}
            loading={loading}
            hasMore={hasMore}
            pitchDict={pitchDict}
            hitterDict={hitterDict}
            hitterOptions={hitterOptions}
            canFilterByHitter={canFilterByHitter}
            newShotID={newShotID}
            updatedShotID={updatedShotID}
            onLoadMore={onLoadMore}
            onSelect={onSelect}
          />
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};

export const MachineShotHistory = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  useEffect(() => {
    const unsubscribe = () => {
      WebSocketService.send(
        WsMsgType.U2S_MachineShotHistory,
        { enable: false, machineID: machine?.machineID },
        COMPONENT_NAME
      );
    };

    WebSocketService.send(
      WsMsgType.U2S_MachineShotHistory,
      { enable: true, machineID: machine?.machineID },
      COMPONENT_NAME
    );

    return unsubscribe;
  }, [machine?.machineID]);

  if (!machine) {
    return null;
  }

  if (!machine.enable_trajekt_vision) {
    return <ShotHistory />;
  }

  return (
    <>
      <ShotHistory />
      <MissedVisionDetections />
    </>
  );
};
